import { Add } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { SwitchButtonWidget } from 'src/components/widget';
import { useGetProductWithVariationsComplete } from 'src/hooks/api/product/useGetAllProduct';
import { ICCAsset } from 'src/interfaces';
import { IProductVariation } from 'src/interfaces/Product';
import { lang } from 'src/utils';
import { getProductPreviewUrl } from 'src/utils/getPreviewUrl';

import { getVariationDisplayText } from './VariationTemplateItem.helper';
import {
  ProductDivider,
  ProductImgContainer,
  RadioContainer,
} from './VariationTemplateItem.styled';

interface VariationTemplateItemProps {
  index: number;
  productCode: string;
  variationCode?: string;
  options?: {
    showRadioGroup?: boolean;
    showActionButton?: boolean;
    showToggle?: boolean;
  };
  showFlavors?: boolean;
  onSelectProduct?: () => void;
  onVariationChange?: (value: string) => void;
  onProductCodeChange?: (value: string) => void;
  onShowFlavorsChange?: (value: boolean) => void;
  assets?: ICCAsset[];
}

export const VariationTemplateItem = memo(
  ({
    index,
    productCode,
    variationCode,
    options = {},
    onSelectProduct,
    onVariationChange,
    onProductCodeChange,
    showFlavors = true,
    onShowFlavorsChange,
    assets,
  }: VariationTemplateItemProps) => {
    const { data: product, isLoading } = useGetProductWithVariationsComplete({
      productCode,
      variationCode,
    });
    const { showRadioGroup, showActionButton } = options;
    const variations = product?.productVariations;
    const currentVariation = useMemo(
      () =>
        variations?.find(
          (variation: IProductVariation) =>
            variation.product_code === variationCode,
        ),
      [variations, variationCode],
    );

    const previewUrl = useMemo(() => {
      return getProductPreviewUrl(product, variationCode, assets);
    }, [assets, product, variationCode]);

    useEffect(() => {
      if (!productCode && product?.product_code) {
        onProductCodeChange && onProductCodeChange(product.product_code);
      }
    }, [product, productCode, onProductCodeChange]);

    const variationTitle = useMemo(
      () =>
        getVariationDisplayText(currentVariation, {
          showFlavorName: index === 1,
          showSizeName: index === 1,
          nameOverride: product?.name_override || '',
        }),
      [currentVariation, index, product?.name_override],
    );

    return (
      <Grid container direction="column" gap={1}>
        <Grid container item gap={1} direction="column">
          <Typography fontWeight="400">
            {lang.productItemLabel(index)}
          </Typography>
          {currentVariation && (
            <Typography fontWeight="400">{variationTitle}</Typography>
          )}
        </Grid>

        {options.showToggle && (
          <Grid>
            <SwitchButtonWidget
              label={lang.flavor}
              value={showFlavors}
              onChange={(value) =>
                onShowFlavorsChange && onShowFlavorsChange(value as boolean)
              }
            />
          </Grid>
        )}

        {!productCode && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            fullWidth
            onClick={onSelectProduct}
            disabled={!isLoading}>
            {lang.addProduct}
          </Button>
        )}

        {isLoading && productCode && <CircularProgress />}

        {productCode && showRadioGroup && variations && !isLoading && (
          <Grid container direction="column" gap={1}>
            <RadioGroup
              onChange={(event) => {
                onVariationChange && onVariationChange(event.target.value);
              }}
              value={variationCode || variations?.[0]?.product_code}>
              {variations.map((variation: IProductVariation) => (
                <RadioContainer key={variation.product_code}>
                  <FormControlLabel
                    value={variation.product_code}
                    control={<Radio />}
                    label={
                      <Typography paddingLeft={1} fontWeight="400">
                        {getVariationDisplayText(variation, {
                          showProductCode: false,
                          showProductName: false,
                          nameOverride: product.name_override || '',
                        })}
                        <Typography
                          color="textSecondary"
                          component="span"
                          fontSize="0.875rem">
                          [{variation.product_code}]
                        </Typography>
                      </Typography>
                    }
                  />
                </RadioContainer>
              ))}
            </RadioGroup>
          </Grid>
        )}

        {productCode && !isLoading && (
          <>
            <Grid container direction="column" gap={2}>
              {previewUrl && (
                <ProductImgContainer>
                  <img src={previewUrl} alt={currentVariation?.name} />
                </ProductImgContainer>
              )}
              {showActionButton && (
                <Button variant="outlined" fullWidth onClick={onSelectProduct}>
                  {lang.replaceProduct}
                </Button>
              )}
            </Grid>
          </>
        )}
        <ProductDivider />
      </Grid>
    );
  },
);
